/**
 * BrowserStorage Implementation
 *
 * Token storage implementation using browser's localStorage or sessionStorage.
 * Includes portability checks to ensure browser APIs are available.
 */
/**
 * Browser-based token storage implementation.
 * Supports both localStorage (persistent) and sessionStorage (session-only).
 *
 * @example
 * ```typescript
 * // Using localStorage (persistent)
 * const storage = new BrowserStorage({ storageType: 'localStorage' });
 *
 * // Using sessionStorage (cleared on tab close)
 * const storage = new BrowserStorage({ storageType: 'sessionStorage' });
 *
 * // Custom key prefix
 * const storage = new BrowserStorage({
 *   storageType: 'localStorage',
 *   keyPrefix: 'myapp_'
 * });
 * ```
 */
export class BrowserStorage {
    storage;
    accessTokenKey;
    refreshTokenKey;
    /**
     * Creates a new BrowserStorage instance.
     *
     * @param config - Configuration options for storage
     * @throws {Error} If the specified storage type is not available in the environment
     */
    constructor(config) {
        const prefix = config.keyPrefix ?? "auth_";
        // Portability check: ensure browser storage API is available
        if (config.storageType === "localStorage") {
            if (typeof window === "undefined" || !window.localStorage) {
                throw new Error("localStorage is not available in this environment");
            }
            this.storage = window.localStorage;
        }
        else {
            if (typeof window === "undefined" || !window.sessionStorage) {
                throw new Error("sessionStorage is not available in this environment");
            }
            this.storage = window.sessionStorage;
        }
        this.accessTokenKey = `${prefix}access_token`;
        this.refreshTokenKey = `${prefix}refresh_token`;
    }
    /**
     * Retrieves the stored access token from browser storage.
     *
     * @returns The access token string, or null if not found
     */
    getAccessToken() {
        return this.storage.getItem(this.accessTokenKey);
    }
    /**
     * Retrieves the stored refresh token from browser storage.
     *
     * @returns The refresh token string, or null if not found
     */
    getRefreshToken() {
        return this.storage.getItem(this.refreshTokenKey);
    }
    /**
     * Stores both access and refresh tokens in browser storage.
     *
     * @param accessToken - The access token to store
     * @param refreshToken - The refresh token to store
     */
    setTokens(accessToken, refreshToken) {
        this.storage.setItem(this.accessTokenKey, accessToken);
        this.storage.setItem(this.refreshTokenKey, refreshToken);
    }
    /**
     * Clears all stored tokens from browser storage.
     */
    clearTokens() {
        this.storage.removeItem(this.accessTokenKey);
        this.storage.removeItem(this.refreshTokenKey);
    }
}
//# sourceMappingURL=BrowserStorage.js.map